import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  TaskDelay,
  TaskStatus,
  TaskTargetType,
  TaskType,
} from '../domain/predefined-task';
import { Task } from '../domain/task';

export interface TaskSchema {
  type: TaskType;
  description: string;
  delay: TaskDelay | null;
  targetType: TaskTargetType;
  status: TaskStatus;
  createdAt: Timestamp;
  updatedAt: Timestamp | null;
}

@Injectable()
export class TaskRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<TaskSchema>('tasks', queryFn);
  }

  static fromSchema(
    schema: TaskSchema,
    dietitianId: string,
    id?: string,
  ): Task {
    return Task.create(
      {
        dietitianId,
        type: schema.type,
        description: schema.description,
        delay: schema.delay ?? undefined,
        targetType: schema.targetType,
        status: schema.status,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt?.toDate() ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  toSchema(entity: Task): TaskSchema {
    return <TaskSchema>{
      type: entity.type,
      description: entity.description,
      delay: entity.delay ?? null,
      targetType: entity.targetType,
      status: entity.status,
      createdAt: entity.createdAt
        ? Timestamp.fromDate(entity.createdAt)
        : Timestamp.now(),
      updatedAt: entity.updatedAt ? Timestamp.fromDate(entity.updatedAt) : null,
    };
  }

  async findAll(dietitianId: string): Promise<Task[]> {
    const snap = await firstValueFrom(this.collection(dietitianId).get());
    return snap.docs.map((doc) =>
      TaskRepository.fromSchema(doc.data(), dietitianId, doc.id),
    );
  }

  async create(dietitianId: string, task: Task): Promise<Task> {
    const schema = this.toSchema(task);
    const created = await this.collection(dietitianId).add(schema);
    return TaskRepository.fromSchema(schema, dietitianId, created.id);
  }

  async update(dietitianId: string, task: Task): Promise<Task> {
    const schema = this.toSchema(task);
    await this.collection(dietitianId).doc(task.id.toString()).set(schema);
    return TaskRepository.fromSchema(schema, dietitianId, task.id.toString());
  }
}
